import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['entry', 'log'];
  static classes = ['entryFading', 'visible'];
  static values = {
    fadeDuration: { type: Number, default: 1000 },
    visibleDuration: { type: Number, default: 30000 }
  };

  connect() {
    this.controllerConnected = true;
    this.entryTimers ||= new Map();
    this.#updateVisibility();
  }

  disconnect() {
    this.controllerConnected = false;
    this.entryTimers?.forEach(({ fade, remove }) => {
      clearTimeout(fade);
      clearTimeout(remove);
    });
    this.entryTimers?.clear();
  }

  entryTargetConnected(entry) {
    this.entryTimers ||= new Map();
    this.#cancelEntryTimers(entry);
    entry.classList.remove(this.entryFadingClass);

    const visibleFor = this.#remainingVisibleDuration(entry);
    if (visibleFor <= 0) {
      entry.remove();
      return;
    }

    entry.dataset.campaignLogActive = 'true';
    this.logTarget.classList.add(this.visibleClass);

    const timers = { fade: null, remove: null };
    timers.fade = setTimeout(() => this.#fadeEntry(entry, timers), visibleFor);
    this.entryTimers.set(entry, timers);
    this.logTarget.scrollTop = this.logTarget.scrollHeight;
  }

  entryTargetDisconnected(entry) {
    this.#cancelEntryTimers(entry);
    queueMicrotask(() => {
      if (!this.controllerConnected) return;

      this.#updateVisibility();
    });
  }

  restoreClientState(event) {
    // Turbo morphs retained entries in place, so Stimulus target callbacks do not reconnect them.
    if (event.target !== this.logTarget) return;

    this.entryTimers?.forEach((timers, entry) => {
      if (!entry.isConnected) return;

      if (timers.remove) {
        delete entry.dataset.campaignLogActive;
        entry.classList.add(this.entryFadingClass);
      } else {
        entry.dataset.campaignLogActive = 'true';
        entry.classList.remove(this.entryFadingClass);
      }
    });

    this.#updateVisibility();
  }

  #remainingVisibleDuration(entry) {
    const createdAt = Date.parse(entry.dataset.createdAt);
    if (!Number.isFinite(createdAt)) return this.visibleDurationValue;

    return Math.max(0, Math.min(this.visibleDurationValue, createdAt + this.visibleDurationValue - Date.now()));
  }

  #fadeEntry(entry, timers) {
    if (!entry.isConnected) return;

    delete entry.dataset.campaignLogActive;
    entry.classList.add(this.entryFadingClass);
    timers.remove = setTimeout(() => entry.remove(), this.fadeDurationValue);
  }

  #cancelEntryTimers(entry) {
    const timers = this.entryTimers?.get(entry);
    if (!timers) return;

    clearTimeout(timers.fade);
    clearTimeout(timers.remove);
    this.entryTimers.delete(entry);
  }

  #updateVisibility() {
    const hasActiveEntry = this.entryTargets.some(
      (entry) => entry.dataset.campaignLogActive === 'true' || entry.classList.contains(this.entryFadingClass)
    );
    this.logTarget.classList.toggle(this.visibleClass, hasActiveEntry);
  }
}
